export default class ResourceVector {
    init() {
        this.worker = 0;
        this.concrete = 0;
        this.metal = 0;
        this.plastic = 0;
        this.fuel = 0;
        return this;
    }

    fill(number) {
        this.worker = number;
        this.concrete = number;
        this.metal = number;
        this.plastic = number;
        this.fuel = number;
        return this;
    }

    initSparse(other) {
        if (other.worker != null) this.worker = other.worker;
        if (other.concrete != null) this.concrete = other.concrete;
        if (other.metal != null) this.metal = other.metal;
        if (other.plastic != null) this.plastic = other.plastic;
        if (other.fuel != null) this.fuel = other.fuel;
        return this;
    }

    scaleVector(factor) {
        if (factor === 1.0) return this;
        this.worker *= factor;
        this.concrete *= factor;
        this.metal *= factor;
        this.plastic *= factor;
        this.fuel *= factor;
        return this;
    }

    scaleVectorSparse(factor) {
        if (factor === 1.0) return this;
        if (this.worker != null) this.worker *= factor;
        if (this.concrete != null) this.concrete *= factor;
        if (this.metal != null) this.metal *= factor;
        if (this.plastic != null) this.plastic *= factor;
        if (this.fuel != null) this.fuel *= factor;
        return this;
    }

    powSparse(exponent) {
        if (typeof (exponent) !== "number") {
            throw new Error("exponent needs to be a number");
        }
        exponent = exponent ? exponent : 2.0;
        if (this.worker != null) this.worker = Math.pow(this.worker, exponent);
        if (this.concrete != null) this.concrete = Math.pow(this.concrete, exponent);
        if (this.metal != null) this.metal = Math.pow(this.metal, exponent);
        if (this.plastic != null) this.plastic = Math.pow(this.plastic, exponent);
        if (this.fuel != null) this.fuel = Math.pow(this.fuel, exponent);
        return this;
    }

    sumUpVector() {
        return this.worker +
            this.concrete +
            this.metal +
            this.plastic +
            this.fuel;
    }

    sumUpVectorSparse() {
        let sum = 0;
        if (this.worker != null) sum += this.worker;
        if (this.concrete != null) sum += this.concrete;
        if (this.metal != null) sum += this.metal;
        if (this.plastic != null) sum += this.plastic;
        if (this.fuel != null) sum += this.fuel;
        return sum;
    }

    copyVector() {
        let res = new ResourceVector();
        res.worker = this.worker;
        res.concrete = this.concrete;
        res.metal = this.metal;
        res.plastic = this.plastic;
        res.fuel = this.fuel;
        return res;
    }

    hasVectorSparse(other) {
        return (other.worker ? other.worker <= this.worker : true)
            && (other.concrete ? other.concrete <= this.concrete : true)
            && (other.metal ? other.metal <= this.metal : true)
            && (other.plastic ? other.plastic <= this.plastic : true)
            && (other.fuel ? other.fuel <= this.fuel : true);
    }

    isEqual(other) {
        return (other.worker === this.worker)
            && (other.concrete === this.concrete)
            && (other.metal === this.metal)
            && (other.plastic === this.plastic)
            && (other.fuel === this.fuel);
    }

    floor() {
        if (this.worker != null) this.worker = Math.floor(this.worker);
        if (this.concrete != null) this.concrete = Math.floor(this.concrete);
        if (this.metal != null) this.metal = Math.floor(this.metal);
        if (this.plastic != null) this.plastic = Math.floor(this.plastic);
        if (this.fuel != null) this.fuel = Math.floor(this.fuel);
        return this;
    }

    ceil() {
        if (this.worker != null) this.worker = Math.ceil(this.worker);
        if (this.concrete != null) this.concrete = Math.ceil(this.concrete);
        if (this.metal != null) this.metal = Math.ceil(this.metal);
        if (this.plastic != null) this.plastic = Math.ceil(this.plastic);
        if (this.fuel != null) this.fuel = Math.ceil(this.fuel);
        return this;
    }

    toNumber() {
        this.worker = Number(this.worker);
        this.concrete = Number(this.concrete);
        this.metal = Number(this.metal);
        this.plastic = Number(this.plastic);
        this.fuel = Number(this.fuel);
        return this;
    }

    isInteger() {
        return Number.isInteger(this.worker) &&
            Number.isInteger(this.concrete) &&
            Number.isInteger(this.metal) &&
            Number.isInteger(this.plastic) &&
            Number.isInteger(this.fuel);
    }

    // --------------------------------------------------------
    // Binary math
    // The sparse version allows the right hand side to be null
    // Left hand side needs to be defined
    // --------------------------------------------------------

    addVector(lhs, rhs) {
        this.worker = lhs.worker + rhs.worker;
        this.concrete = lhs.concrete + rhs.concrete;
        this.metal = lhs.metal + rhs.metal;
        this.plastic = lhs.plastic + rhs.plastic;
        this.fuel = lhs.fuel + rhs.fuel;
        return this;
    }

    addVectorSparse(lhs, rhs) {
        if (rhs.worker != null) this.worker = lhs.worker + rhs.worker;
        if (rhs.concrete != null) this.concrete = lhs.concrete + rhs.concrete;
        if (rhs.metal != null) this.metal = lhs.metal + rhs.metal;
        if (rhs.plastic != null) this.plastic = lhs.plastic + rhs.plastic;
        if (rhs.fuel != null) this.fuel = lhs.fuel + rhs.fuel;
        return this;
    }

    subVector(lhs, rhs) {
        this.worker = lhs.worker - rhs.worker;
        this.concrete = lhs.concrete - rhs.concrete;
        this.metal = lhs.metal - rhs.metal;
        this.plastic = lhs.plastic - rhs.plastic;
        this.fuel = lhs.fuel - rhs.fuel;
        return this;
    }

    subVectorSparse(lhs, rhs) {
        if (rhs.worker != null) this.worker = lhs.worker - rhs.worker;
        if (rhs.concrete != null) this.concrete = lhs.concrete - rhs.concrete;
        if (rhs.metal != null) this.metal = lhs.metal - rhs.metal;
        if (rhs.plastic != null) this.plastic = lhs.plastic - rhs.plastic;
        if (rhs.fuel != null) this.fuel = lhs.fuel - rhs.fuel;
        return this;
    }

    // --------------------------------------------------------
    // Iteration
    // --------------------------------------------------------

    forEachSparse(foo) {
        let idx = 0;
        if (this.worker != null) foo({ id: "worker", amount: this.worker }, idx++);
        if (this.concrete != null) foo({ id: "concrete", amount: this.concrete }, idx++);
        if (this.metal != null) foo({ id: "metal", amount: this.metal }, idx++);
        if (this.plastic != null) foo({ id: "plastic", amount: this.plastic }, idx++);
        if (this.fuel != null) foo({ id: "fuel", amount: this.fuel }, idx++);
    }

    mapSparse(foo) {
        let res = [];
        let idx = 0;
        if (this.worker != null) res.push(foo({ id: "worker", amount: this.worker }, idx++));
        if (this.concrete != null) res.push(foo({ id: "concrete", amount: this.concrete }, idx++));
        if (this.metal != null) res.push(foo({ id: "metal", amount: this.metal }, idx++));
        if (this.plastic != null) res.push(foo({ id: "plastic", amount: this.plastic }, idx++));
        if (this.fuel != null) res.push(foo({ id: "fuel", amount: this.fuel }, idx++));
        return res;
    }
}
